import { createWebHistory, createRouter } from "vue-router";
import posthog from "posthog-js";

// all routes need names for us to properly push page transitions to PostHog
const routes = [
  {
    name: "Account",
    path: "/account",
    redirect: "/account/o",
    component: () => import("../layouts/Account.vue"),
    children: [
      {
        path: "/account/w",
        redirect: "/account/o",
      },
      {
        name: "OrganizationChooser",
        path: "/account/o",
        component: () => import("../views/account/OrganizationChooser.vue"),
      },
      {
        path: "/account/o/:organizationHashedId",
        component: () => import("../views/account/OrganizationLobbyLayout.vue"),
        children: [
          {
            name: "OrganizationLobbyHome",
            path: "",
            component: () =>
              import("../views/account/OrganizationLobbyHome.vue"),
          },
          {
            name: "OrganizationReporting",
            path: "reporting/:subtab?",
            component: () =>
              import("../views/account/OrganizationReporting.vue"),
          },
        ],
      },
      {
        name: "OrganizationSettings",
        path: "/account/o/:organizationHashedId/settings",
        component: () => import("../views/account/OrganizationSettings.vue"),
      },
      {
        name: "AccountSettings",
        path: "/account/settings",
        component: () => import("../views/account/Settings.vue"),
      },
      {
        path: "/account/w/:collaborationSpaceId",
        redirect: (to) => {
          return {
            name: "ClientCollaborationSpaceDashboard",
            params: { collaborationSpaceId: to.params.collaborationSpaceId },
          };
        },
      },
      {
        name: "ClientCollaborationSpaceDashboard",
        path: "/account/w/:collaborationSpaceId/dashboard",
        component: () =>
          import("../views/collaborationSpace/ClientDashboard.vue"),
      },
      {
        name: "CollaborationSpaceReporting",
        path: "/account/w/:collaborationSpaceId/reporting/:subtab?",
        component: () => import("../views/collaborationSpace/Reporting.vue"),
      },
      {
        name: "ProfileList",
        path: "/account/profiles",
        component: () => import("../views/collaborationSpace/ProfileList.vue"),
      },
      {
        name: "Connections",
        path: "/account/connections",
        component: () => import("../views/account/Connections.vue"),
      },
      {
        name: "Messages",
        path: "/account/messages",
        component: () => import("../views/account/Messages.vue"),
      },
      {
        name: "MessageThread",
        path: "/account/messages/:messageThreadId",
        component: () => import("../views/account/Messages.vue"),
      },
      {
        name: "Notifications",
        path: "/account/notifications",
        component: () => import("../views/account/Notifications.vue"),
      },
      {
        name: "ClientResources",
        path: "/account/resources",
        component: () => import("../views/account/Resources.vue"),
      },
      {
        name: "ClientResourceDetail",
        path: "/account/resources/:slug",
        component: () => import("../views/account/Resources.vue"),
      },
      {
        name: "CollaborationSpaceSettings",
        path: "/account/w/:collaborationSpaceId/settings",
        component: () => import("../views/collaborationSpace/Settings.vue"),
      },
      {
        name: "EditProfile",
        path: "/account/edit-profile",
        component: () => import("../views/account/EditProfile.vue"),
      },
      {
        name: "PrivacyPreferences",
        path: "/account/privacy-preferences",
        component: () => import("../views/account/PrivacyPreferences.vue"),
      },
      {
        name: "MatchingPreferences",
        path: "/account/matching-preferences",
        component: () => import("../views/account/MatchingPreferences.vue"),
      },
      {
        name: "Payments",
        path: "/account/payments",
        component: () => import("../views/account/Payments.vue"),
      },
      {
        name: "SiteList",
        path: "/account/sites",
        component: () => import("../views/account/SiteList.vue"),
      },
      {
        name: "SiteDetail",
        path: "/account/sites/:hashedId",
        component: () => import("../views/account/SiteDetail.vue"),
      },
      {
        name: "ClientProjectDetail",
        path: "/account/w/:collaborationSpaceId/work/:projectId",
        component: () => import("../views/project/ProjectDetail.vue"),
      },
      {
        name: "FirmDetail",
        path: "/account/firm/:hashedId",
        component: () => import("../views/account/FirmDetail.vue"),
      },
      {
        name: "FirmSettings",
        path: "/account/firm/:hashedId/settings",
        component: () => import("../views/account/FirmSettings.vue"),
      },
      {
        name: "ExpertProjectDetail",
        path: "/account/dashboard/projects/:projectId",
        component: () => import("../views/project/ProjectDetail.vue"),
      },
      {
        name: "CollaborationSpaceResearchCompassWizard",
        path: "/account/w/:collaborationSpaceId/research-compass/",
        meta: {
          hideContactButton: true,
        },
        component: () =>
          import("../views/collaborationSpace/ResearchCompassWizardv2.vue"),
      },
      {
        name: "CollaborationSpaceResearchCompassDetail",
        path: "/account/w/:collaborationSpaceId/research-compass/:researchCompassId",
        meta: {
          hideContactButton: true,
        },
        component: () =>
          import("../views/collaborationSpace/ResearchCompassWizardv2.vue"),
      },
      {
        name: "CollaborationRequestDetail",
        path: "/account/w/:collaborationSpaceId/request/:collaborationRequestId",
        component: () =>
          import("../views/collaborationSpace/CollaborationRequestDetail.vue"),
      },
      {
        name: "CollaborationRequestDetailProfile",
        path: "/account/w/:collaborationSpaceId/request/:collaborationRequestId/profile/:profileId",
        component: () =>
          import("../views/collaborationSpace/CollaborationRequestDetail.vue"),
      },
      {
        name: "SdsJobTypeDetail",
        path: "/account/w/:collaborationSpaceId/sds/:machineName",
        component: () =>
          import("../views/collaborationSpace/SdsJobTypeDetail.vue"),
      },
      {
        name: "SdsJobDetail",
        path: "/account/w/:collaborationSpaceId/sds/:machineName/:sdsJobId",
        component: () => import("../views/collaborationSpace/SdsJobDetail.vue"),
      },
      {
        name: "CreateWaypointReport",
        path: "/account/w/:collaborationSpaceId/waypoint-reports/create",
        meta: {
          hideContactButton: true,
        },
        component: () =>
          import("../views/collaborationSpace/CreateEditWaypointReport.vue"),
      },
      {
        name: "EditWaypointReport",
        path: "/account/w/:collaborationSpaceId/waypoint-reports/:waypointReportId",
        meta: {
          hideContactButton: true,
        },
        component: () =>
          import("../views/collaborationSpace/CreateEditWaypointReport.vue"),
      },
      {
        name: "ResearcherDashboardProjects",
        path: "/account/dashboard/projects",
        component: () => import("../views/researcherDashboard/Projects.vue"),
      },
      {
        name: "ResearcherDashboardSds",
        path: "/account/dashboard/sds",
        component: () => import("../views/researcherDashboard/Sds.vue"),
      },
      {
        name: "ResearcherDashboardSdsJobDetail",
        path: "/account/dashboard/sds/:hashedId",
        component: () => import("../views/researcherDashboard/SdsJob.vue"),
      },
      {
        name: "ResearcherDashboardIdentityVerification",
        path: "/account/dashboard/identity-verification",
        component: () =>
          import("../views/researcherDashboard/IdentityVerification.vue"),
      },
      {
        name: "ResearcherDashboardResearchCompass",
        path: "/account/dashboard/research-compass-list",
        component: () =>
          import("../views/researcherDashboard/ResearchCompass.vue"),
      },
      {
        name: "ResearcherDashboardResearchCompassWizard",
        path: "/account/dashboard/research-compass",
        component: () =>
          import("../views/researcherDashboard/ResearchCompassWizardv2.vue"),
      },
      {
        name: "ResearcherDashboardResearchCompassDetail",
        path: "/account/dashboard/research-compass/:researchCompassId",
        component: () =>
          import("../views/researcherDashboard/ResearchCompassWizardv2.vue"),
      },
      {
        name: "ResearcherDashboardResources",
        path: "/account/dashboard/resources",
        component: () => import("../views/researcherDashboard/Resources.vue"),
      },
      {
        name: "ResearchCommunityList",
        path: "/community",
        component: () =>
          import("../views/researchCommunity/ResearchCommunityList.vue"),
      },
      {
        name: "ResearchCommunityGuidelines",
        path: "/community/guidelines",
        component: () =>
          import("../views/researchCommunity/ResearchCommunityGuidelines.vue"),
      },
      {
        name: "ResearchCommunity",
        path: "/community/:slug",
        component: () =>
          import("../views/researchCommunity/ResearchCommunity.vue"),
      },
      {
        name: "RsearchCommunityPost",
        path: "/community/:slug/:postId",
        component: () =>
          import("../views/researchCommunity/ResearchCommunity.vue"),
      },
      {
        name: "ResearchCommunitySettings",
        path: "/community/:slug/settings",
        component: () =>
          import("../views/researchCommunity/ResearchCommunitySettings.vue"),
      },
      {
        name: "ResearchCommunityInviteAdmin",
        path: "/community/:slug/invites",
        component: () =>
          import("../views/researchCommunity/ResearchCommunityInviteAdmin.vue"),
      },
      {
        name: "MeetingScheduler",
        path: "/account/meeting/:meetingId/schedule",
        component: () => import("../views/meeting/Scheduler.vue"),
      },
    ],
  },
  {
    path: "/setup",
    component: () => import("../layouts/Setup.vue"),
    children: [
      {
        name: "AccountSetup",
        path: "/setup",
        component: () => import("../views/account/AccountSetup.vue"),
      },
    ],
  },
  {
    path: "/feed",
    component: () => import("../layouts/Feed.vue"),
    children: [
      {
        name: "FeedSearch",
        path: "/feed",
        component: () => import("../views/feed/FeedSearch.vue"),
      },
      {
        name: "ManageFeedSearchSubscriptionList",
        path: "/feed/manage",
        component: () =>
          import("../views/feed/ManageFeedSearchSubscriptionList.vue"),
      },
      {
        name: "ManageFeedSearchSubscription",
        path: "/feed/manage/:hashedId",
        component: () =>
          import("../views/feed/ManageFeedSearchSubscription.vue"),
      },
    ],
  },
  {
    path: "/feedback",
    component: () => import("../layouts/Feedback.vue"),
    children: [
      {
        name: "FeedbackSurvey",
        path: "/feedback",
        component: () => import("../views/feedback/FeedbackSurvey.vue"),
      },
    ],
  },
  {
    path: "/meeting",
    component: () => import("../layouts/Meeting.vue"),
    children: [
      {
        path: "/meeting/:meetingId",
        component: () => import("../views/meeting/Meeting.vue"),
      },
    ],
  },
  {
    path: "/meeting-request/:hashedId/:securityToken",
    component: () => import("../layouts/Basic.vue"),
    children: [
      {
        name: "MeetingRequestResponse",
        path: "",
        component: () => import("../views/meeting/MeetingRequestResponse.vue"),
      },
    ],
  },
  {
    path: "/credit-transfer-request/:hashedId/:token",
    component: () => import("../layouts/Basic.vue"),
    children: [
      {
        name: "CreditTransferRequestResponse",
        path: "",
        component: () =>
          import("../views/account/CreditTransferRequestResponse.vue"),
      },
    ],
  },
  {
    path: "/meeting/callback/:provider",
    component: () => import("../layouts/Basic.vue"),
    children: [
      {
        name: "MeetingCallback",
        path: "",
        component: () => import("../views/meeting/MeetingCallback.vue"),
      },
    ],
  },
  {
    path: "/teams/callback",
    component: () => import("../layouts/Basic.vue"),
    children: [
      {
        name: "TeamsCallback",
        path: "",
        component: () => import("../views/teams/TeamsCallback.vue"),
      },
    ],
  },
  {
    path: "/content",
    component: () => import("../layouts/Basic.vue"),
    children: [
      {
        path: "/content/map",
        component: () => import("../views/Map.vue"),
      },
      {
        path: "/content/marketing-animation",
        component: () => import("../views/MarketingAnimation.vue"),
      },
    ],
  },
  {
    path: "/research-compass-dark",
    component: () => import("../layouts/DarkResearchCompass.vue"),
    children: [
      {
        path: "/legacy-map/:authorId?",
        component: () =>
          import("../views/researchCompass/ResearchCompassLegacyMap.vue"),
      },
      {
        path: "/research-compass/legacy-map/:authorId?",
        redirect: (to) => {
          return {
            path: `/legacy-map${to.params.authorId ? `/${to.params.authorId}` : ""}`,
            query: to.query,
          };
        },
      },
      {
        path: "/research-compass/paper/:corpusId",
        component: () =>
          import("../views/researchCompass/ResearchCompassPaper.vue"),
      },
      {
        path: "/research-compass/patent/:patentId",
        component: () =>
          import("../views/researchCompass/ResearchCompassPatent.vue"),
      },
      {
        path: "/research-compass/author/:authorId",
        component: () =>
          import("../views/researchCompass/ResearchCompassAuthor.vue"),
      },
    ],
  },
  {
    path: "/research-compass",
    component: () => import("../layouts/PublicResearchCompass.vue"),
    children: [
      {
        path: "/research-compass/:researchCompassId/:viewKey",
        component: () =>
          import("../views/researchCompass/PublicResearchCompass.vue"),
      },
    ],
  },
  {
    path: "/auth",
    redirect: "/auth/login",
    component: () => import("../layouts/Auth.vue"),
    children: [
      {
        name: "Terms",
        path: "/terms",
        component: () => import("../views/Terms.vue"),
      },
      {
        name: "Privacy",
        path: "/privacy",
        component: () => import("../views/Privacy.vue"),
      },
      {
        name: "Login",
        path: "/auth/login",
        component: () => import("../views/auth/Login.vue"),
      },
      {
        name: "Logout",
        path: "/auth/logout",
        component: () => import("../views/auth/Logout.vue"),
      },
      {
        name: "ForgotPassword",
        path: "/auth/forgot-password",
        component: () => import("../views/auth/ForgotPassword.vue"),
      },
      {
        name: "ResetPassword",
        path: "/auth/reset-password/:hashedId/:code",
        component: () => import("../views/auth/ResetPassword.vue"),
      },
      {
        name: "SetPassword",
        path: "/auth/set-password/:hashedId/:code",
        component: () => import("../views/auth/SetPassword.vue"),
      },
      {
        name: "Register",
        path: "/auth/register",
        component: () => import("../views/auth/Register.vue"),
      },
      {
        name: "ExpertRegister",
        path: "/auth/expert-register",
        component: () => import("../views/auth/Register.vue"),
        props: { restrictedPersonType: "EXPERT" },
      },
      {
        name: "ClientRegister",
        path: "/auth/client-register",
        component: () => import("../views/auth/Register.vue"),
        props: { restrictedPersonType: "CLIENT" },
      },
      {
        name: "MagicLink",
        path: "/auth/link/:hashedId/:code",
        component: () => import("../views/auth/MagicLink.vue"),
      },
      {
        name: "VerifyEmail",
        path: "/auth/verify-email/:hashedId/:code",
        component: () => import("../views/auth/VerifyEmail.vue"),
      },
      {
        name: "VerifyInquiryEmail",
        path: "/auth/verify-collaboration-inquiry-email/:hashedId/:code",
        component: () =>
          import("../views/auth/VerifyCollaborationInquiryEmail.vue"),
      },
      {
        name: "AcceptInvitation",
        path: "/auth/accept-invitation/:code",
        component: () => import("../views/auth/AcceptInvitation.vue"),
      },
    ],
  },
  {
    path: "/auth/setup",
    component: () => import("../layouts/Auth.vue"),
    props: { loggedIn: true },
    children: [
      {
        name: "AuthSetup",
        path: "/auth/setup",
        component: () => import("../views/auth/AuthSetup.vue"),
      },
    ],
  },
  {
    name: "AuthRedirect",
    path: "/",
    redirect: "/auth/login",
  },
  { name: "GeneralRedirect", path: "/:pathMatch(.*)*", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    const noScrollRoutes = [
      "OrganizationLobbyHome",
      "OrganizationReporting",
      "ClientCollaborationSpaceDashboard",
      "CollaborationSpaceReporting",
    ];
    if (
      noScrollRoutes.includes(to.name) &&
      noScrollRoutes.includes(from.name)
    ) {
      return false;
    }
    return { top: 0 };
  },
});

router.beforeEach((to, from) => {
  if (from.path !== to.path) {
    posthog.capture("$pageleave");
  }
});

export default router;
